import { useState, type ChangeEvent, type FC } from 'react';

import { useDateFormatter } from '@mint-lib/formatting-hooks';
import {
  CalendarSingle,
  DatepickerInput,
  DatepickerInputHelperText,
  DatepickerInputLabel,
  DatepickerInputRoot,
  type Matcher,
} from '@mint-lib/tw-components/patterns';

type Props = {
  id: string;
  label?: string;
  a11yLabel: string;
  value: Date | undefined;
  error?: string;
  disabled?: boolean;
  clearable?: boolean;
  disabledDates?: Matcher;
  onApply: (date?: Date) => void;
  onClear?: () => void;
};

const DatePicker: FC<Props> = ({
  id,
  label,
  a11yLabel,
  value,
  error,
  disabled = false,
  clearable = true,
  disabledDates,
  onApply,
  onClear,
}) => {
  const { formattedDateFrom: formattedDate } = useDateFormatter(value);

  const [isOpen, setIsOpen] = useState(false);
  const [localDate, setLocalDate] = useState(value);

  const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value && clearable) {
      setLocalDate(undefined);
      onClear?.();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setLocalDate(value);
    }
    setIsOpen(open);
  };

  const handleSelect = (date?: Date) => {
    if (!date) {
      return;
    }

    setLocalDate(date);
  };

  const handleApply = (date?: Date) => {
    onApply(date);
    setIsOpen(false);
  };

  return (
    <DatepickerInputRoot data-dd-action-name={a11yLabel}>
      {label ? (
        <DatepickerInputLabel label={label} htmlFor={id} disabled={disabled} />
      ) : null}
      <DatepickerInput
        fullWidth
        variant="single"
        align="start"
        value={formattedDate}
        clearable={clearable}
        disabled={disabled}
        color={error ? 'error' : 'default'}
        open={isOpen}
        onOpenChange={handleOpenChange}
        onChangeInput={handleChangeInput}
      >
        <CalendarSingle
          selected={localDate}
          onApply={handleApply}
          onSelect={handleSelect}
          disabledDates={disabledDates}
        />
      </DatepickerInput>
      {error ? (
        <DatepickerInputHelperText
          helperText={error}
          color="error"
          disabled={disabled}
        />
      ) : null}
    </DatepickerInputRoot>
  );
};

export default DatePicker;
